import { Typography } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import buildMinorsSection from './MinorsSection';

function buildHouseholdMembersSection({
  isAffordable,
  isProspect,
  affordablePrograms,
  propertyStateCode,
}) {
  return {
    name: 'householdMembers',
    title: 'Household Members',
    container: 'default',
    fields: [
      {
        variant: 'custom',
        component: (
          <Typography variant="bodyLarge" color="text.secondary">
            List all household members who will live in the residence during the
            next 12 months. Be sure to list any temporarily absent members.
          </Typography>
        ),
        GridProps: {
          xs: 12,
          paddingLeft: 0,
        },
      },
      {
        variant: 'singleSelect',
        name: 'maritalStatus',
        label: 'Marital Status',
        options: [
          {
            text: 'Single',
            value: 'single',
          },
          {
            text: 'Married',
            value: 'married',
          },
          {
            text: 'Divorced',
            value: 'divorced',
          },
          {
            text: 'Separated',
            value: 'separated',
          },
          {
            text: 'Widowed',
            value: 'widowed',
          },
        ],
        changeActions: [
          {
            type: 'updateField',
            target: 'spouseFirstName',
            when: {
              eq: 'single',
            },
            value: null,
          },
          {
            type: 'updateField',
            target: 'spouseMiddleName',
            when: {
              eq: 'single',
            },
            value: null,
          },
          {
            type: 'updateField',
            target: 'spouseLastName',
            when: {
              eq: 'single',
            },
            value: null,
          },
          {
            type: 'updateField',
            target: 'spousePreferredName',
            when: {
              eq: 'single',
            },
            value: null,
          },
        ],
        rules: {
          required: true,
        },
        GridProps: {
          xs: 12,
          md: 3,
          paddingLeft: 0,
        },
      },
      {
        name: 'spouseSection',
        title: 'SPOUSE',
        container: 'collapse',
        watchFields: ['maritalStatus'],
        hidden: ({ getValues }) =>
          ['married', 'separated', 'divorced', 'widowed'].includes(
            getValues('maritalStatus'),
          ) === false,
        GridProps: {
          xs: 12,
        },
        fields: [
          {
            variant: 'text',
            name: 'spouseFirstName',
            label: 'Legal First Name',
            GridProps: {
              xs: 12,
              sm: 3,
            },
          },
          {
            variant: 'text',
            name: 'spouseMiddleName',
            label: 'Middle Name',
            GridProps: {
              xs: 12,
              sm: 3,
            },
          },
          {
            variant: 'text',
            name: 'spouseLastName',
            label: 'Legal Last Name',
            GridProps: {
              xs: 12,
              sm: 3,
            },
          },
          {
            variant: 'text',
            name: 'spousePreferredName',
            label: 'Preferred Name',
            GridProps: {
              xs: 12,
              sm: 3,
            },
          },
        ],
      },
      {
        variant: 'radioGroup',
        name: 'adultsOver18',
        label: 'Are there adults over 18?',
        options: [
          {
            label: 'Yes',
            value: 'yes',
          },
          {
            label: 'No',
            value: 'no',
          },
        ],
        changeActions: [
          {
            type: 'updateField',
            target: 'howManyAdditionalOccupants',
            when: {
              eq: 'no',
            },
            value: null,
          },
          {
            type: 'updateField',
            target: 'additionalOccupantsDescription',
            when: {
              eq: 'no',
            },
            value: '',
          },
        ],
        rules: {
          required: true,
        },
        sx: {
          flexDirection: 'row',
        },
        GridProps: {
          xs: 12,
          paddingLeft: 0,
        },
      },
      ...(isProspect
        ? [
            {
              variant: 'counter',
              name: 'howManyAdditionalOccupants',
              label: 'How many additional occupants?',
              watchFields: ['adultsOver18'],
              hidden: ({ getValues }) => getValues('adultsOver18') !== 'yes',
              rules: {
                required: true,
                min: 1,
              },
              GridProps: {
                xs: 12,
                paddingLeft: 0,
              },
            },
            ...(isAffordable
              ? [
                  {
                    variant: 'text',
                    name: 'additionalOccupantsDescription',
                    label:
                      'If any of the occupants listed are Live-in Caretakers,' +
                      ' Disabled or Foster Adults, please detail below',
                    placeholder: 'Please explain...',
                    multiline: true,
                    rows: 3,
                    watchFields: ['adultsOver18'],
                    hidden: ({ getValues }) =>
                      getValues('adultsOver18') !== 'yes',
                    GridProps: {
                      xs: 12,
                      paddingLeft: 0,
                    },
                  },
                ]
              : []),
          ]
        : []),
      {
        variant: 'banner',
        status: 'info',
        message:
          'Note: All occupants over 18 must complete separate lease applications.',
        GridProps: { xs: 12, paddingLeft: 0 },
      },
      ...(isAffordable && isProspect
        ? [
            {
              variant: 'radioGroup',
              name: 'anticipateChangeInHousehold',
              label:
                'Do you anticipate a change in household occupants in the next 12 months?',
              options: [
                {
                  label: 'Yes',
                  value: 'yes',
                },
                {
                  label: 'No',
                  value: 'no',
                },
              ],
              changeActions: [
                {
                  type: 'updateField',
                  target: 'howManyPregnancy',
                  when: {
                    eq: 'no',
                  },
                  value: 0,
                },
                {
                  type: 'updateField',
                  target: 'howManyAdoption',
                  when: {
                    eq: 'no',
                  },
                  value: 0,
                },
                {
                  type: 'updateField',
                  target: 'howManyFoster',
                  when: {
                    eq: 'no',
                  },
                  value: 0,
                },
              ],
              rules: {
                required: true,
              },
              sx: {
                flexDirection: 'row',
              },
              GridProps: {
                xs: 12,
                paddingLeft: 0,
              },
            },
            {
              variant: 'custom',
              component: (
                <Typography variant="bodySmall">
                  If “Yes”, how many additional occupants are expected due to:
                </Typography>
              ),
              watchFields: ['anticipateChangeInHousehold'],
              hidden: ({ getValues }) =>
                getValues('anticipateChangeInHousehold') !== 'yes',
              GridProps: {
                xs: 12,
                paddingLeft: 0,
              },
            },
            {
              variant: 'counter',
              name: 'howManyPregnancy',
              label: 'Pregnancy',
              defaultValue: 0,
              rules: {
                required: true,
                min: 0,
              },
              watchFields: ['anticipateChangeInHousehold'],
              hidden: ({ getValues }) =>
                getValues('anticipateChangeInHousehold') !== 'yes',
              GridProps: {
                xs: 3,
                paddingLeft: 0,
              },
            },
            {
              variant: 'counter',
              name: 'howManyAdoption',
              label: 'Adoption',
              defaultValue: 0,
              rules: {
                required: true,
                min: 0,
              },
              watchFields: ['anticipateChangeInHousehold'],
              hidden: ({ getValues }) =>
                getValues('anticipateChangeInHousehold') !== 'yes',
              GridProps: {
                xs: 3,
              },
            },
            {
              variant: 'counter',
              name: 'howManyFoster',
              label: 'Foster Children',
              defaultValue: 0,
              rules: {
                required: true,
                min: 0,
              },
              watchFields: ['anticipateChangeInHousehold'],
              hidden: ({ getValues }) =>
                getValues('anticipateChangeInHousehold') !== 'yes',
              GridProps: {
                xs: 6,
              },
            },
            {
              variant: 'radioGroup',
              name: 'doesUnitNeedToBeAccessible',
              label: 'Does the unit need to be accessible?',
              options: [
                {
                  label: 'Yes',
                  value: 'yes',
                },
                {
                  label: 'No',
                  value: 'no',
                },
              ],
              rules: {
                required: true,
              },
              sx: {
                flexDirection: 'row',
              },
              GridProps: {
                xs: 12,
                paddingLeft: 0,
              },
            },
          ]
        : []),
      {
        variant: 'custom',
        component: <div style={{ height: 5 }} />,
      },
      buildMinorsSection({
        isProspect,
        isAffordable,
        affordablePrograms,
        propertyStateCode,
      }),
    ],
  };
}

export default buildHouseholdMembersSection;
